import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, subMonths } from 'date-fns';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { isMobile } from 'react-device-detect';
import classNames from 'classnames';

import { Page } from 'src/features/layout/Page';
import { Button } from 'src/components/Button';
import { HosLogsItem } from 'src/components/HosLogsItem';
import { RangeFilter } from 'src/features/hosLogs/rangeFilter/RangeFilter';
import { Filter } from 'src/features/hosLogs/filter';
import { FilterItem } from 'src/components/FilterItem';
import { PageSidebar } from 'src/components/PageSidebar/PageSidebar';
import { AppButton } from 'src/components/AppButton/AppButton';

import FilterIcon from 'src/assets/hosLogs/filter.svg';
import CalendarIcon from 'src/assets/hosLogs/calendar.svg';

import { getHosLogs, getHosFilter, getHosRangeFilter } from './store';
import {
  setFilter,
  setFilterFormState,
  resetFilter,
  setRangeFilter,
  resetRangeFilter,
  fetchCalendarHosLogsAction,
} from './store/hosLogStore';
import './hosLogs.scss';
import '../../features/hosLogs/hosLog/components/HosLogActionsBar/hosLogActionsBar.scss';
import 'src/features/hosLogs/hosLog/components';
import { fetchHosLogs } from './store/hosLogStore';
import { HosLog } from './types';
import { get } from 'lodash';
import { getDriverInfo, getUserId } from 'src/features/profile/store';
import { ExportAllLogs } from 'src/features/hosLogs/exportAllLogs';
import HosLogsActionsBar from './hosLog/components/HosLogActionsBar/HosLogActionsBar';
import { SHOW_HOS_LOGS_ACTIVITY_SUMMARY } from 'src/constants/featureFlags';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TimersContainer } from 'src/components/Timers/TimersContainer';
import { fetchCalendarDotsLogsCount } from 'src/configs/gui-config';
import { HosLogBanners } from './logBanner/HosLogBanners';
import { fetchProposedMovements, fetchUnassignedMovements } from '../movements/store/movementStore';
import { AssignmentStatus } from 'src/common/types';
import moment from 'moment';

enum FilterName {
  VIOLATION = 'violationsFilter',
  EXCEPTION = 'exceptionsFilter',
  PENDING = 'pendingFilter',
  REJECTED = 'rejectedFilter',
  MISSING = 'missingFilter',
  UNCERTIFIED = 'uncertifiedFilter',
}

export const HosLogs: React.FC = () => {
  const dispatch = useDispatch();

  const [startDateStore, endDateStore] = useSelector(getHosRangeFilter);
  const prevStartDateRef = useRef(startDateStore);
  const prevEndDateRef = useRef(endDateStore);

  const [showRangeFilter, setShowRangeFilter] = useState<boolean>(false);
  const [initiated, setInitiated] = useState(false);

  const nodeRef = useRef(null);

  const employeeId = useSelector(getUserId);

  const showHOSLogActivitySummary = useFlags()[SHOW_HOS_LOGS_ACTIVITY_SUMMARY];

  const powerUnitNumber = localStorage.getItem('powerUnitNumber');
  const driverInfo = useSelector(getDriverInfo);

  useEffect(() => {
    if (startDateStore && endDateStore) {
      const startDate = moment(startDateStore, moment.tz.guess(false))
        .set({ hour: 0, minute: 0, second: 0 })
        .toISOString();
      const endDate = moment(endDateStore, moment.tz.guess(false))
        .set({ hour: 23, minute: 59, second: 59 })
        .toISOString();

      if (powerUnitNumber && driverInfo) {
        dispatch(
          fetchUnassignedMovements({
            companyId: driverInfo.companyId,
            powerUnitNumber: powerUnitNumber,
            startTimestamp: startDate,
            endTimestamp: endDate,
            status: AssignmentStatus.UNASSIGNED,
          }),
        );
      }

      if (driverInfo) {
        dispatch(
          fetchProposedMovements({
            employeeId: driverInfo.employeeId,
            startTimestamp: startDate,
            endTimestamp: endDate,
            assignmentStatus: AssignmentStatus.PROPOSED,
          }),
        );
      }
    }
  }, [dispatch, powerUnitNumber, driverInfo, startDateStore, endDateStore]);

  const fetchHosLogHandler = useCallback((): void => {
    if (employeeId) {
      dispatch(
        fetchHosLogs({
          startDate: format(startDateStore, 'yyyy-MM-dd'),
          endDate: format(endDateStore, 'yyyy-MM-dd'),
          employeeIds: employeeId,
          count: 100,
          startIndex: 0,
          includeExemptLogs: true,
        }),
      );
    }
  }, [dispatch, startDateStore, endDateStore, employeeId]);

  useEffect(() => {
    if (employeeId) {
      dispatch(
        fetchCalendarHosLogsAction({
          startDate: format(startOfMonth(subMonths(new Date(), 6)), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
          employeeIds: employeeId,
          count: fetchCalendarDotsLogsCount,
          startIndex: 0,
        }),
      );
    }
  }, [employeeId, dispatch]);

  useEffect(() => {
    if (!initiated && employeeId) {
      fetchHosLogHandler();
      setInitiated(true);
    }
  }, [fetchHosLogHandler, initiated, employeeId]);

  const hosLogs = useSelector(getHosLogs);
  const hosFilter = useSelector(getHosFilter);

  const [filterModal, setFilterModal] = useState(false);
  const [displayLogs, setDisplayLogs] = useState<HosLog[]>([]);

  const hosLogsLength = useMemo(() => hosLogs.length, [hosLogs]);

  useEffect(() => {
    let logs = hosLogs;
    const filters = Object.keys(hosFilter).filter((prop: string) => get(hosFilter, prop));
    if (filters.length > 0) {
      logs = hosLogs.filter(item => {
        return (
          filters.filter(filter => {
            if (filter === FilterName.VIOLATION && item.violationCount > 0) return true;
            if (filter === FilterName.EXCEPTION && item.exceptions.length > 0) return true;
            if (filter === FilterName.UNCERTIFIED && item.certifiedStatus === 0) return true;
            if (
              filter === FilterName.PENDING &&
              item.editActivity &&
              item.editActivity.status === 'pending'
            ) {
              return true;
            }

            if (
              filter === FilterName.REJECTED &&
              item.editActivity &&
              item.editActivity.status === 'rejected'
            ) {
              return true;
            }

            return false;
          }).length > 0
        );
      });
    }

    setDisplayLogs(logs);
  }, [hosLogs, hosFilter]);

  const removeFilter = useCallback(
    (filterName: string): (() => void) => {
      return (): void => {
        dispatch(
          setFilter({
            ...hosFilter,
            [filterName]: false,
          }),
        );
        dispatch(
          setFilterFormState({
            ...hosFilter,
            [filterName]: false,
          }),
        );
      };
    },
    [dispatch, hosFilter],
  );

  const renderFilterBlock = (label: string, filterName: FilterName): JSX.Element | undefined => {
    if (hosFilter[filterName] === true) {
      return <FilterItem title={label} onClick={removeFilter(filterName)} />;
    }
  };

  const handleFilterModalClose = useCallback(() => {
    setFilterModal(false);
  }, [setFilterModal]);

  const handleFilterModalOpen = useCallback(() => {
    setFilterModal(true);
  }, [setFilterModal]);

  const openRangeFilterHandler = useCallback((): void => setShowRangeFilter(true), [
    setShowRangeFilter,
  ]);

  const resetEmptyRangeFilter = useCallback((): void => {
    if (!startDateStore || !endDateStore) {
      dispatch(setRangeFilter([prevStartDateRef.current, prevEndDateRef.current]));
    }
  }, [startDateStore, endDateStore, prevStartDateRef, prevEndDateRef, dispatch]);

  const closeRangeFilterHandler = useCallback((): void => {
    dispatch(setRangeFilter([prevStartDateRef.current, prevEndDateRef.current]));
    setShowRangeFilter(false);
  }, [setShowRangeFilter, prevStartDateRef, prevEndDateRef, dispatch]);

  const onChangeRangeFilterHandler = useCallback(
    ([startRange, endRange]: [Date, Date]): void => {
      dispatch(setRangeFilter([startRange, endRange]));
    },
    [dispatch],
  );

  const onApplyRangeFilter = useCallback((): void => {
    prevStartDateRef.current = startDateStore;
    prevEndDateRef.current = endDateStore;
    fetchHosLogHandler();
    setShowRangeFilter(false);
  }, [fetchHosLogHandler, setShowRangeFilter, startDateStore, endDateStore]);

  const onResetRangeFilter = useCallback((): void => {
    dispatch(resetRangeFilter());
    if (employeeId) {
      onApplyRangeFilter();
    }
  }, [dispatch, employeeId]); // Please do not add onApplyRangeFilter to deps

  const onResetFilter = useCallback((): void => {
    dispatch(resetFilter());
  }, [dispatch]);

  const onResetAllFilters = useCallback((): void => {
    onResetRangeFilter();
    onResetFilter();
  }, [onResetFilter, onResetRangeFilter]);

  const calendarButtonTitle = useMemo(() => {
    const startLabel = startDateStore ? format(startDateStore, 'MMM, dd, yyyy') : '';
    const endLabel = endDateStore ? format(endDateStore, 'MMM, dd, yyyy') : '';
    return `${startLabel} - ${endLabel}`;
  }, [startDateStore, endDateStore]);

  const renderRangeFilter = () => {
    return (
      <RangeFilter
        closeRangeFilterHandler={closeRangeFilterHandler}
        onResetRangeFilter={onResetRangeFilter}
        onApplyRangeFilter={onApplyRangeFilter}
        startDate={startDateStore}
        endDate={endDateStore}
        onChangeRangeFilterHandler={onChangeRangeFilterHandler}
      />
    );
  };

  const renderFilter = () => <Filter onClose={handleFilterModalClose} />;

  return (
    <Page>
      <div className={classNames('hos-logs', { 'is-desktop': !isMobile })}>
        {!isMobile && (
          <PageSidebar>
            {renderRangeFilter()}
            {renderFilter()}
            <div className="hos-logs__sidebar-buttons">
              <AppButton title="Reset" secondary small onClick={onResetAllFilters} />
            </div>
          </PageSidebar>
        )}
        <div className="hos-log-and-action-bar-wrapper">
          {showHOSLogActivitySummary && !isMobile && <HosLogsActionsBar />}
          <div className="hos-logs__main-content">
            {isMobile && (
              <>
                <div className="hos-logs__title">Hours Of Service Logs</div>
                <div className="hos-logs__button-group">
                  <div className="hos-logs__button-header">
                    <Button
                      title="Filter"
                      icon={FilterIcon}
                      onClick={handleFilterModalOpen}
                      className="hos-logs__button-filter"
                    />
                    <Button
                      title={calendarButtonTitle}
                      icon={CalendarIcon}
                      className="hos-logs__button-calendar"
                      onClick={openRangeFilterHandler}
                    />
                  </div>

                  <div className="hos-logs__button-selected-filter">
                    {renderFilterBlock('Violations', FilterName.VIOLATION)}
                    {renderFilterBlock('Exceptions', FilterName.EXCEPTION)}
                    {renderFilterBlock('Pending Edit', FilterName.PENDING)}
                    {renderFilterBlock('Rejected Edit', FilterName.REJECTED)}
                    {renderFilterBlock('Misssing Info', FilterName.MISSING)}
                    {renderFilterBlock('Uncertified', FilterName.UNCERTIFIED)}
                  </div>
                </div>
              </>
            )}
            <TimersContainer isMobile={isMobile} />
            {isMobile && <HosLogBanners />}
            <div className="hos-logs__button-export-log">
              <ExportAllLogs
                hosLogsLength={hosLogsLength}
                hosLogsStartDate={startDateStore && format(startDateStore, 'yyyy-MM-dd')}
                hosLogsEndDate={endDateStore && format(endDateStore, 'yyyy-MM-dd')}
                employeeId={employeeId}
              />
              <span className="hos-logs__text">{`Showing ${displayLogs.length} of ${hosLogs.length}`}</span>
            </div>
            {displayLogs.map((item: HosLog) => {
              return (
                <HosLogsItem
                  key={item.date}
                  item={item}
                  resetEmptyRangeFilter={resetEmptyRangeFilter}
                />
              );
            })}
          </div>
        </div>
      </div>

      {isMobile && (
        <TransitionGroup>
          {showRangeFilter && (
            <CSSTransition nodeRef={nodeRef} timeout={400} classNames="filter-transition">
              <div className="filter-transition__wrapper" ref={nodeRef}>
                {renderRangeFilter()}
              </div>
            </CSSTransition>
          )}
          {filterModal && (
            <CSSTransition nodeRef={nodeRef} timeout={400} classNames="filter-transition">
              <div className="filter-transition__wrapper" ref={nodeRef}>
                {renderFilter()}
              </div>
            </CSSTransition>
          )}
        </TransitionGroup>
      )}
    </Page>
  );
};
